import { TOOLTIP_SIDES, TOOLTIP_CONSTANTS } from '../constants';
import React from 'react';

const sectorToSide = [
  [TOOLTIP_SIDES.BOTTOM_RIGHT, TOOLTIP_SIDES.BOTTOM, TOOLTIP_SIDES.BOTTOM_LEFT],
  [TOOLTIP_SIDES.RIGHT, TOOLTIP_SIDES.TOP, TOOLTIP_SIDES.LEFT],
  [TOOLTIP_SIDES.TOP_RIGHT, TOOLTIP_SIDES.TOP, TOOLTIP_SIDES.TOP_LEFT]
];

const calcRow = (topRatio: number, bottomRatio: number, innerY: number, refY: number): number => {
  let row;
  if (refY < innerY * topRatio) {
    row = TOOLTIP_CONSTANTS.ZERO;
  } else if (refY > innerY * bottomRatio) {
    row = TOOLTIP_CONSTANTS.TWO;
  } else {
    row = TOOLTIP_CONSTANTS.ONE;
  }
  return row;
};

const calcCol = (innerX: number, refX: number, halfBubble: number, bubbleWithFloatyWidth: number): number => {
  let col;
  if (innerX > refX + halfBubble && refX > halfBubble && innerX < refX + bubbleWithFloatyWidth) {
    col = TOOLTIP_CONSTANTS.ONE;
  } else if (refX < halfBubble || innerX > refX + bubbleWithFloatyWidth) {
    col = TOOLTIP_CONSTANTS.ZERO;
  } else if (refX > halfBubble && innerX < refX + halfBubble) {
    col = TOOLTIP_CONSTANTS.TWO;
  } else {
    col = TOOLTIP_CONSTANTS.ONE;
  }
  return col;
};

export const calcSide = (
  innerX: number,
  innerY: number,
  refX: number,
  refY: number,
  bubbleWidth: number
): TOOLTIP_SIDES => {
  const halfBubble = bubbleWidth / TOOLTIP_CONSTANTS.TWO + TOOLTIP_CONSTANTS.HALF_BUBBLE_OFFSET;
  const bubbleWithFloatyWidth = bubbleWidth + TOOLTIP_CONSTANTS.FLOAT_Y_WIDTH;
  let row;
  const col = calcCol(innerX, refX, halfBubble, bubbleWithFloatyWidth);
  if (col === TOOLTIP_CONSTANTS.TWO || (col === TOOLTIP_CONSTANTS.ZERO && innerX < refX + bubbleWithFloatyWidth)) {
    row = calcRow(TOOLTIP_CONSTANTS.RATIO_HALF, TOOLTIP_CONSTANTS.RATIO_HALF, innerY, refY);
  } else {
    row = calcRow(TOOLTIP_CONSTANTS.RATIO_ONE_THIRD, TOOLTIP_CONSTANTS.RATIO_TWO_THIRDS, innerY, refY);
  }
  return sectorToSide[row][col];
};

export const showFloaty = (side: TOOLTIP_SIDES): boolean =>
  [
    TOOLTIP_SIDES.TOP,
    TOOLTIP_SIDES.TOP_LEFT,
    TOOLTIP_SIDES.TOP_RIGHT,
    TOOLTIP_SIDES.BOTTOM,
    TOOLTIP_SIDES.BOTTOM_LEFT,
    TOOLTIP_SIDES.BOTTOM_RIGHT
  ].indexOf(side) !== -1;

export const getNubPos = (side: TOOLTIP_SIDES): React.CSSProperties => {
  switch (side) {
    case TOOLTIP_SIDES.BOTTOM:
      return {
        top: `calc(100% + ${TOOLTIP_CONSTANTS.OFFSET_BASE_Y / TOOLTIP_CONSTANTS.TWO}px)`,
        left: `calc(50% - ${TOOLTIP_CONSTANTS.OFFSET_BASE_X / TOOLTIP_CONSTANTS.TWO}px)`
      };
    case TOOLTIP_SIDES.BOTTOM_RIGHT:
      return {
        top: `calc(100% + ${TOOLTIP_CONSTANTS.OFFSET_BASE_Y / TOOLTIP_CONSTANTS.TWO}px)`,
        right: 0
      };
    case TOOLTIP_SIDES.BOTTOM_LEFT:
      return {
        top: `calc(100% + ${TOOLTIP_CONSTANTS.OFFSET_BASE_Y / TOOLTIP_CONSTANTS.TWO}px)`,
        left: 0
      };
    case TOOLTIP_SIDES.RIGHT:
      return {
        top: '50%',
        left: `calc(100% + ${TOOLTIP_CONSTANTS.OFFSET_BASE_X}px)`
      };
    case TOOLTIP_SIDES.LEFT:
      return {
        top: '50%',
        right: `calc(100% + ${TOOLTIP_CONSTANTS.OFFSET_BASE_X}px)`
      };
    case TOOLTIP_SIDES.TOP:
      return {
        bottom: `calc(100% + ${TOOLTIP_CONSTANTS.OFFSET_BASE_Y / TOOLTIP_CONSTANTS.THREE}px)`,
        left: `calc(50% - ${TOOLTIP_CONSTANTS.OFFSET_BASE_X / TOOLTIP_CONSTANTS.TWO}px)`
      };
    case TOOLTIP_SIDES.TOP_RIGHT:
      return {
        bottom: `calc(100% + ${TOOLTIP_CONSTANTS.OFFSET_BASE_Y / TOOLTIP_CONSTANTS.THREE}px)`,
        right: 0
      };
    case TOOLTIP_SIDES.TOP_LEFT:
      return {
        bottom: `calc(100% + ${TOOLTIP_CONSTANTS.OFFSET_BASE_Y / TOOLTIP_CONSTANTS.THREE}px)`,
        left: 0
      };
    default:
      return {
        top: 0,
        left: 0
      };
  }
};

export const getBubPos = (side: TOOLTIP_SIDES, bubbleWidth: number): React.CSSProperties => {
  switch (side) {
    case TOOLTIP_SIDES.BOTTOM:
      return {
        top: `calc(100% + ${TOOLTIP_CONSTANTS.OFFSET_BASE_Y - TOOLTIP_CONSTANTS.TOP_OFFSET}px)`,
        left: `calc(50% - ${Math.round(bubbleWidth / TOOLTIP_CONSTANTS.TWO)}px)`,
        width: bubbleWidth
      };
    case TOOLTIP_SIDES.BOTTOM_RIGHT:
      return {
        top: `calc(100% + ${TOOLTIP_CONSTANTS.OFFSET_BASE_Y - TOOLTIP_CONSTANTS.TOP_OFFSET}px)`,
        left: `calc(100% - ${TOOLTIP_CONSTANTS.OFFSET_BASE_X * TOOLTIP_CONSTANTS.TWO}px)`,
        width: bubbleWidth
      };
    case TOOLTIP_SIDES.BOTTOM_LEFT:
      return {
        top: `calc(100% + ${TOOLTIP_CONSTANTS.OFFSET_BASE_Y - TOOLTIP_CONSTANTS.TOP_OFFSET}px)`,
        right: `calc(100% - ${TOOLTIP_CONSTANTS.OFFSET_BASE_X * TOOLTIP_CONSTANTS.TWO}px)`,
        width: bubbleWidth
      };
    case TOOLTIP_SIDES.RIGHT:
      return {
        top: '50%',
        left: `calc(100% + ${TOOLTIP_CONSTANTS.OFFSET_BASE_X * TOOLTIP_CONSTANTS.TWO}px)`,
        width: bubbleWidth
      };
    case TOOLTIP_SIDES.LEFT:
      return {
        top: '50%',
        right: `calc(100% + ${TOOLTIP_CONSTANTS.OFFSET_BASE_X * TOOLTIP_CONSTANTS.TWO}px)`,
        width: bubbleWidth
      };
    case TOOLTIP_SIDES.TOP:
      return {
        bottom: `calc(100% + ${TOOLTIP_CONSTANTS.OFFSET_BASE_Y / TOOLTIP_CONSTANTS.TWO}px)`,
        left: `calc(50% - ${Math.round(bubbleWidth / TOOLTIP_CONSTANTS.TWO)}px)`,
        width: bubbleWidth
      };
    case TOOLTIP_SIDES.TOP_RIGHT:
      return {
        bottom: `calc(100% + ${TOOLTIP_CONSTANTS.OFFSET_BASE_Y / TOOLTIP_CONSTANTS.TWO}px)`,
        left: `calc(100% - ${TOOLTIP_CONSTANTS.OFFSET_BASE_X * TOOLTIP_CONSTANTS.TWO}px)`,
        width: bubbleWidth
      };
    case TOOLTIP_SIDES.TOP_LEFT:
      return {
        bottom: `calc(100% + ${TOOLTIP_CONSTANTS.OFFSET_BASE_Y / TOOLTIP_CONSTANTS.TWO}px)`,
        right: `calc(100% - ${TOOLTIP_CONSTANTS.OFFSET_BASE_X * TOOLTIP_CONSTANTS.TWO}px)`,
        width: bubbleWidth
      };
    default:
      return {
        top: 0,
        left: 0,
        width: bubbleWidth
      };
  }
};

export const getSide = (toggleRef, side: TOOLTIP_SIDES, bubbleWidth: number): TOOLTIP_SIDES => {
  const bounds = toggleRef.current.getBoundingClientRect();
  const { x, y } = bounds;
  return side || calcSide(window.innerWidth, window.innerHeight, x, y, bubbleWidth);
};
